import { useMemo, useState } from 'react';
import CopyButton from '../../../components/CopyButton';

const encodeBase64 = (value: string) => {
    const encodedBytes = new TextEncoder().encode(value);
    const binaryString = Array.from(encodedBytes, (byte) => String.fromCharCode(byte)).join('');
    return btoa(binaryString);
};

const decodeBase64 = (value: string) => {
    const binaryString = atob(value);
    const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
};

export const Base64Tool = () => {
    const [inputValue, setInputValue] = useState('');
    const [outputValue, setOutputValue] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const hasInput = useMemo(() => inputValue.trim().length > 0, [inputValue]);

    const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInputValue(event.target.value);
        setErrorMessage('');
    };

    const handleEncode = () => {
        if (!hasInput) {
            setOutputValue('');
            setErrorMessage('Enter text to encode.');
            return;
        }

        try {
            setOutputValue(encodeBase64(inputValue));
            setErrorMessage('');
        } catch (error) {
            console.error('Base64 encode error', error);
            setOutputValue('');
            setErrorMessage('Could not encode text.');
        }
    };

    const handleDecode = () => {
        if (!hasInput) {
            setOutputValue('');
            setErrorMessage('Enter Base64 to decode.');
            return;
        }

        try {
            setOutputValue(decodeBase64(inputValue));
            setErrorMessage('');
        } catch (error) {
            console.error('Base64 decode error', error);
            setOutputValue('');
            setErrorMessage('Invalid Base64 string.');
        }
    };

    const handleClear = () => {
        setInputValue('');
        setOutputValue('');
        setErrorMessage('');
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Base64 Encoder / Decoder</h2>
            <div className="flex flex-col gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="base64-input">
                        Input
                    </label>
                    <textarea
                        id="base64-input"
                        className="w-full min-h-[140px] rounded-md border border-gray-300 p-3 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        placeholder="Paste text or Base64 here..."
                        value={inputValue}
                        onChange={handleInputChange}
                    />
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={handleEncode}
                        className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    >
                        Encode
                    </button>
                    <button
                        type="button"
                        onClick={handleDecode}
                        className="inline-flex items-center justify-center rounded-md border border-blue-600 px-4 py-2 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    >
                        Decode
                    </button>
                    <button
                        type="button"
                        onClick={handleClear}
                        className="inline-flex items-center justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
                    >
                        Clear
                    </button>
                    <CopyButton
                        text={outputValue}
                        label="Copy"
                        size="md"
                        variant="outline"
                        disabled={!outputValue}
                    />
                </div>
                {errorMessage ? (
                    <p className="text-sm text-red-600">{errorMessage}</p>
                ) : null}
                <div>
                    <div className="flex items-center justify-between gap-4 mb-1">
                        <label className="block text-sm font-medium text-gray-700" htmlFor="base64-output">
                            Output
                        </label>
                    </div>
                    <textarea
                        id="base64-output"
                        className="w-full min-h-[140px] rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-900"
                        value={outputValue}
                        readOnly
                    />
                </div>
            </div>
        </div>
    );
};
